import type { JSX } from 'preact';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import type { Product } from '@/domain/Product.ts';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import PromotionGroupItems from './parts/PromotionGroupItems.tsx';
import type { CurrencyFormatConfig } from '@/domain/Pricing.ts';
import TermsDetails from '../SharedDrawerComponents/TermsDetails.tsx';
import { trackPromotionsModalInteraction } from '../../Tracking.ts';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand.tsx';

export type DiscoverPromotionsDrawerProps = CurrencyFormatConfig & {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  trackingSource: 'sku' | 'bundle' | 'subscription';
};

export default function DiscoverPromotionsDrawer({
  isOpen,
  onClose,
  product,
  currencyCode,
  locale,
  trackingSource
}: DiscoverPromotionsDrawerProps) {
  const { t } = useTranslations();

  const { promotions: promotionGroups = [] } = product;

  const termsUrl =
    promotionGroups.length > 0 ? promotionGroups[0].termsUrl : '';
  const description =
    promotionGroups.length > 0 ? (promotionGroups[0].description ?? '') : '';
  const subText =
    promotionGroups.length > 0 ? (promotionGroups[0].subText ?? '') : '';

  return (
    <ModalOnDemand
      heading={t('productDetailsHero.drawers.discoverPromotionsDrawer.title')}
      position="side"
      isOpen={isOpen}
      onClose={onClose}
      closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
    >
      {description &&
        ((<FormattedText>{description}</FormattedText>) as JSX.Element)}

      {
        (
          <PromotionGroupItems
            promotions={promotionGroups}
            productInfo={product}
            currencyCode={currencyCode}
            locale={locale}
          />
        ) as JSX.Element
      }

      {subText && ((<FormattedText>{subText}</FormattedText>) as JSX.Element)}

      {termsUrl &&
        ((
          <TermsDetails
            label={t(
              'productDetailsHero.drawers.discoverPromotionsDrawer.seeTerms'
            )}
            loadingText={t(
              'productDetailsHero.drawers.discoverPromotionsDrawer.termsLoading'
            )}
            url={termsUrl}
            onToggled={() =>
              trackPromotionsModalInteraction(
                `${trackingSource} promo info click`,
                product,
                {
                  key: 'productDetailsHero.drawers.discoverPromotionsDrawer.seeTerms',
                  value: t(
                    'productDetailsHero.drawers.discoverPromotionsDrawer.seeTerms'
                  )
                }
              )
            }
          />
        ) as JSX.Element)}
    </ModalOnDemand>
  );
}
